/**
 * useTreeMap Custom Hook
 *
 * Manages all business logic for the Tree Map component including:
 * - Context-based hierarchy loading
 * - Sphere CRUD operations (via context tree API)
 * - Navigation and drill-down
 * - Type conversion
 * - AI integration
 * - Pagination (via context children API)
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useTreeMapSurface } from './useTreeMapSurface';
import type { Context, RuntimeFilterEnvelope } from 'types/context';
import type { TreeMapViewConfig, BaseViewConfig } from 'types/contextView';
import type {
  TreeMapNavigationState,
  BreadcrumbItem,
  ConversionRules,
  AIAssistanceResponse,
  AISuggestion,
  UseTreeMapReturn,
  Sphere,
} from './types';

interface UseTreeMapOptions {
  context: Context;  // Required - must be provided by parent (e.g., TreeMapOrganism)
  viewConfig: TreeMapViewConfig;
  baseConfig: BaseViewConfig;
  rootSphereId?: string;
  organizationId: string;
  defaultPageSize?: number;
  cardView?: 'minimal' | 'standard' | 'expanded';
  /** Per-level runtime filters (EAINT-781, `useLevelFilters`) — a change re-executes the tree query. */
  runtimeFilters?: RuntimeFilterEnvelope;
}

/**
 * Main useTreeMap hook
 */
export function useTreeMap(options: UseTreeMapOptions): UseTreeMapReturn {
  const { context, viewConfig: _viewConfig, baseConfig: _baseConfig, rootSphereId, organizationId, defaultPageSize = 5, cardView = 'standard', runtimeFilters } = options;

  const apiClients = useApiClientsOrNull();

  // Local state
  const [navigation, setNavigation] = useState<TreeMapNavigationState>({
    rootSphereId: rootSphereId || null,
    rootColumnLevel: null,
    breadcrumbs: [],
    history: [],
  });
  const [error, setError] = useState<string | null>(null);

  const surfaceHook = useTreeMapSurface({
    context,
    rootSphereId: navigation.rootSphereId || rootSphereId || '',
    organizationId,
    apiClient: apiClients?.graphClient || null,
    defaultPageSize,
    cardView,
    runtimeFilters
  });

  // ============================================================================
  // Root Sphere Validation
  // ============================================================================

  /**
   * Validate root sphere for prescriptive mode
   */
  const validateRootSphere = useCallback(async () => {
    if (!rootSphereId || !apiClients || !context) {
      return;
    }

    try {
      const response = await apiClients.graphClient.get<any>(`/spheres/${rootSphereId}`);
      const rootSphere = response.sphere || response.data || response;

      // Validate context levels
      const levels = context.definition?.levels;
      if (levels && levels.length > 0) {
        const firstLevel = levels[0];
        const levelFilters = firstLevel.filters;
        if (levelFilters) {
          // Filters will determine what gets loaded server-side
        }
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(`Failed to load root sphere: ${message}`);
    }
  }, [rootSphereId, apiClients, context]);

  // Validate root sphere when dependencies are ready
  const hasValidatedRef = useRef(false);
  useEffect(() => {
    if (context && apiClients && rootSphereId && !hasValidatedRef.current) {
      hasValidatedRef.current = true;
      validateRootSphere();
    }
  }, [context, apiClients, rootSphereId, validateRootSphere]);

  // ============================================================================
  // Sphere Loading (delegated to surface hook)
  // ============================================================================

  // Load initial data when context loads
  // Skip reload if we're just focusing on an existing sphere (isFocusOnly flag)
  const isFocusOnlyRef = useRef(false);
  const isInitialLoadRef = useRef(true);
  const hasLoadedInitialDataRef = useRef(false);

  useEffect(() => {
    // Skip data loading if this is just a focus navigation
    if (isFocusOnlyRef.current) {
      isFocusOnlyRef.current = false;
      return;
    }

    // Skip if we've already loaded initial data (prevents React StrictMode double-render issues)
    if (hasLoadedInitialDataRef.current && isInitialLoadRef.current) {
      return;
    }

    if (context && apiClients && navigation.rootSphereId) {
      hasLoadedInitialDataRef.current = true;
      isInitialLoadRef.current = false;
      surfaceHook.loadInitialData();
    }
  }, [context, apiClients, navigation.rootSphereId]);

  // Track the highest detail level we've loaded data for
  // Only reload if requesting a HIGHER detail level than we've ever loaded
  const detailLevels = { minimal: 1, standard: 2, expanded: 3 };
  const highestLoadedLevelRef = useRef<number>(detailLevels[cardView]);
  const prevCardViewRef = useRef<'minimal' | 'standard' | 'expanded'>(cardView);
  const cardViewEffectMountedRef = useRef(false);

  // Reload data when cardView (detail level) changes to a HIGHER level than we've ever loaded
  useEffect(() => {
    // Skip the first render - initial data is loaded by the other effect
    if (!cardViewEffectMountedRef.current) {
      cardViewEffectMountedRef.current = true;
      //console.log('🌳 [TreeMap] CardView effect mounted - skipping initial render');
      return;
    }

    // Skip if cardView hasn't actually changed
    if (prevCardViewRef.current === cardView) {
      //console.log('🌳 [TreeMap] CardView unchanged - skipping');
      return;
    }

    const newLevel = detailLevels[cardView];
    const prevCardView = prevCardViewRef.current;
    prevCardViewRef.current = cardView; // Update ref immediately

    // Only reload if going to a HIGHER detail level than we've ever loaded
    if (newLevel > highestLoadedLevelRef.current) {
      if (context && apiClients && navigation.rootSphereId) {
        surfaceHook.loadInitialData();
        highestLoadedLevelRef.current = newLevel;
      }
    }
  }, [cardView, context, apiClients, navigation.rootSphereId]);

  // Re-execute the tree query whenever the composed per-level runtime filters
  // (EAINT-781 `useLevelFilters`, owned by TreeMapOrganism) change — e.g. a
  // `LevelFilterPopoverOrganism` save. Skipped on mount: `loadInitialData`
  // above already fetches with whatever `runtimeFilters` resolves to on first
  // render (persisted session/user/view clauses included).
  const runtimeFiltersMountedRef = useRef(false);
  const runtimeFiltersKeyRef = useRef('');
  useEffect(() => {
    const key = runtimeFilters ? JSON.stringify(runtimeFilters) : '';
    if (!runtimeFiltersMountedRef.current) {
      runtimeFiltersMountedRef.current = true;
      runtimeFiltersKeyRef.current = key;
      return;
    }
    if (key === runtimeFiltersKeyRef.current) return;
    runtimeFiltersKeyRef.current = key;
    if (context && apiClients && navigation.rootSphereId) {
      surfaceHook.reloadWithFilters();
    }
  }, [runtimeFilters, context, apiClients, navigation.rootSphereId]);

  // ============================================================================
  // Navigation
  // ============================================================================

  /**
   * Drill down into a specific sphere
   */
  const drillDown = useCallback((sphereId: string) => {
    const sphere = Object.values(surfaceHook.spheresByColumn)
      .flat()
      .find((s) => s.id === sphereId);

    if (!sphere) return;

    //console.log('🎯 [TreeMap] Drilling down to sphere:', sphere.properties.name, sphereId);

    setNavigation((prev) => ({
      ...prev,
      rootSphereId: sphereId,
      breadcrumbs: [
        ...prev.breadcrumbs,
        {
          label: sphere.properties.name,
          sphereId: sphereId,
          icon: sphere.properties.icon,
        },
      ],
      history: [...prev.history, JSON.stringify(prev)],
    }));

    // DON'T clear data - just recalculate layout from existing spheres
    //console.log('🎯 [TreeMap] Navigation updated, layout will recalculate');
  }, [surfaceHook]);

  /**
   * Focus on a sphere (reorganize view and reload data)
   * Sets the sphere as the new root and loads all its descendants
   */
  const focusOnSphere = useCallback((sphereId: string) => {
    const sphere = Object.values(surfaceHook.spheresByColumn)
      .flat()
      .find((s) => s.id === sphereId);

    if (!sphere) return;

    //console.log('🎯 [TreeMap] Focusing on sphere:', sphere.properties.name, sphereId);

    // Set isFocusOnlyRef flag to skip data reload - we already have all the data loaded
    // The reorganizeForNewRoot function will reorganize existing spheres by column
    isFocusOnlyRef.current = true;

    setNavigation((prev) => ({
      ...prev,
      rootSphereId: sphereId,
      breadcrumbs: [
        ...prev.breadcrumbs,
        {
          label: sphere.properties.name,
          sphereId: sphereId,
          icon: sphere.properties.icon,
        },
      ],
      history: [...prev.history, JSON.stringify(prev)],
    }));

    //console.log('🎯 [TreeMap] Focus navigation updated, will use existing data (no reload)');
  }, [surfaceHook]);

  /**
   * Drill down into a specific column level
   */
  const drillDownColumn = useCallback((columnLevel: number) => {
    const levels = context?.definition?.levels;
    const levelLabel = levels?.[columnLevel]?.label;
    const label = levelLabel?.default || `Level ${columnLevel}`;

    setNavigation((prev) => ({
      ...prev,
      rootColumnLevel: columnLevel,
      breadcrumbs: [
        {
          label,
          columnLevel: columnLevel,
        },
      ],
      history: [...prev.history, JSON.stringify(prev)],
    }));
  }, [context]);

  /**
   * Navigate back to previous view
   */
  const navigateBack = useCallback(() => {
    setNavigation((prev) => {
      if (prev.history.length === 0) return prev;

      const previousState = JSON.parse(prev.history[prev.history.length - 1]);
      return {
        ...previousState,
        history: prev.history.slice(0, -1),
      };
    });
  }, []);

  /**
   * Navigate to a specific breadcrumb
   */
  const navigateToBreadcrumb = useCallback((item: BreadcrumbItem, index: number) => {
    //console.log('🏠 [TreeMap] Navigate to breadcrumb:', { item, index });

    setNavigation((prev) => {
      if (index === -1) {
        // Home - reset to original root
        //console.log('🏠 [TreeMap] Resetting to original root');
        return {
          rootSphereId: null, // Will fall back to prop rootSphereId
          rootColumnLevel: null,
          breadcrumbs: [],
          history: [],
        };
      }

      const newBreadcrumbs = prev.breadcrumbs.slice(0, index + 1);
      const targetBreadcrumb = prev.breadcrumbs[index];

      //console.log('🏠 [TreeMap] Navigating to:', targetBreadcrumb);

      return {
        rootSphereId: targetBreadcrumb.sphereId || null,
        rootColumnLevel: targetBreadcrumb.columnLevel || null,
        breadcrumbs: newBreadcrumbs,
        history: prev.history.slice(0, index),
      };
    });
  }, []);

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  /**
   * Create a new sphere
   */
  const createSphere = useCallback(
    async (parentId: string, schemaType: string, properties: Record<string, any>): Promise<Sphere> => {
      if (!apiClients) throw new Error('API clients not available');

      setError(null);

      try {
        const newSphere = await apiClients.graphClient.post('/spheres', {
          schema_type: schemaType,
          parent_id: parentId,
          properties,
        });

        // Refresh the relevant column (optimistic updates not yet implemented)
        return newSphere;
      } catch (err: any) {
        console.error('Failed to create sphere:', err);
        setError(err.message || 'Failed to create sphere');
        throw err;
      }
    },
    [apiClients]
  );

  /**
   * Update an existing sphere
   */
  const updateSphere = useCallback(
    async (sphereId: string, properties: Record<string, any>): Promise<Sphere> => {
      if (!apiClients) throw new Error('API clients not available');

      setError(null);

      try {
        const updatedSphere = await apiClients.graphClient.patch(`/spheres/${sphereId}`, {
          properties,
        });

        // Find which level this sphere is at and refresh it
        const level = Object.entries(surfaceHook.spheresByColumn).find(([_, spheres]) =>
          spheres.some((s) => s.id === sphereId)
        );

        if (level) {
          await surfaceHook.refreshLevel(Number.parseInt(level[0], 10));
        }

        return updatedSphere;
      } catch (err: any) {
        console.error('Failed to update sphere:', err);
        setError(err.message || 'Failed to update sphere');
        throw err;
      }
    },
    [apiClients, surfaceHook]
  );

  /**
   * Delete a sphere
   */
  const deleteSphere = useCallback(
    async (sphereId: string): Promise<void> => {
      if (!apiClients) throw new Error('API clients not available');

      setError(null);

      try {
        await apiClients.graphClient.delete(`/spheres/${sphereId}`);

        // Find which level this sphere is at and refresh it
        const level = Object.entries(surfaceHook.spheresByColumn).find(([_, spheres]) =>
          spheres.some((s) => s.id === sphereId)
        );

        if (level) {
          await surfaceHook.refreshLevel(Number.parseInt(level[0], 10));
        }
      } catch (err: any) {
        console.error('Failed to delete sphere:', err);
        setError(err.message || 'Failed to delete sphere');
        throw err;
      }
    },
    [apiClients, surfaceHook]
  );

  /**
   * Move a sphere to a new parent (reparent)
   */
  const moveSphere = useCallback(
    async (sphereId: string, targetParentId: string): Promise<void> => {
      if (!apiClients) throw new Error('API clients not available');

      setError(null);

      try {
        await apiClients.graphClient.post(`/relationships/${sphereId}`, {
          relationship_type: 'parent_child',
          target_id: targetParentId,
        });

        // Refresh affected columns (optimistic updates not yet implemented)
      } catch (err: any) {
        console.error('Failed to move sphere:', err);
        setError(err.message || 'Failed to move sphere');
        throw err;
      }
    },
    [apiClients]
  );

  /**
   * Convert a sphere to a different type
   */
  const convertSphere = useCallback(
    async (sphereId: string, targetType: string, targetParentId: string): Promise<Sphere> => {
      if (!apiClients) throw new Error('API clients not available');

      setError(null);

      try {
        const convertedSphere = await apiClients.graphClient.post(`/spheres/${sphereId}/convert`, {
          target_type: targetType,
          target_parent_id: targetParentId,
        });

        // Update local state (proper update logic not yet implemented)
        return convertedSphere;
      } catch (err: any) {
        console.error('Failed to convert sphere:', err);
        setError(err.message || 'Failed to convert sphere');
        throw err;
      }
    },
    [apiClients]
  );

  // ============================================================================
  // Type Conversion Helpers
  // ============================================================================

  /**
   * Check if conversion is possible
   * Currently returns true - conversion rules checking not yet implemented
   */
  const canConvert = useCallback((sphereId: string, targetType: string): boolean => {
    return true;
  }, []);

  /**
   * Get conversion rules for a type pair
   * Currently returns default rules - actual conversion rules retrieval not yet implemented
   */
  const getConversionRules = useCallback(
    (sourceType: string, targetType: string): ConversionRules | null => {
      return {
        allowed: true,
        field_mapping: {},
        required_fields: [],
        workflow_behavior: 'cancel_and_create',
      };
    },
    []
  );

  // ============================================================================
  // Pagination
  // ============================================================================

  /**
   * Load more children for a parent
   */
  const loadMoreChildren = useCallback(
    async (parentId: string, schemaType: string): Promise<void> => {
      if (!context) return;

      // Find which level this parent is at
      const parentLevel = Object.entries(surfaceHook.spheresByColumn).find(([_, spheres]) =>
        spheres.some((s) => s.id === parentId)
      );

      if (!parentLevel) {
        return;
      }

      const level = Number.parseInt(parentLevel[0], 10);
      const paginationState = surfaceHook.paginationState[parentId];

      if (paginationState?.hasMore && paginationState.nextToken) {
        await surfaceHook.loadChildren(parentId, level, {
          token: paginationState.nextToken,
          mode: 'append'
        });
      }
    },
    [context, surfaceHook]
  );

  /**
   * Load all children for a parent (recursively loads until no more pages)
   */
  const loadAllChildren = useCallback(
    async (parentId: string, schemaType: string): Promise<void> => {
      let hasMore = true;
      while (hasMore) {
        await loadMoreChildren(parentId, schemaType);
        const paginationState = surfaceHook.paginationState[parentId];
        hasMore = paginationState?.hasMore || false;
      }
    },
    [loadMoreChildren, surfaceHook]
  );

  // ============================================================================
  // AI Integration
  // ============================================================================

  /**
   * Get AI suggestions for a parent
   * Currently returns empty suggestions - AI integration not yet implemented
   */
  const getSuggestions = useCallback(
    async (parentId: string, schemaType: string, userMessage?: string): Promise<AIAssistanceResponse> => {
      if (!apiClients) throw new Error('API clients not available');

      return {
        suggestions: [],
        explanation: '',
      };
    },
    [apiClients]
  );

  /**
   * Accept an AI suggestion and create sphere
   */
  const acceptSuggestion = useCallback(
    async (parentId: string, suggestion: AISuggestion): Promise<Sphere> => {
      return createSphere(parentId, suggestion.properties.type, suggestion.properties);
    },
    [createSphere]
  );

  /**
   * Refresh a specific level in the TreeMap after a sphere is updated
   */
  const refreshLevel = useCallback(
    async (sphereId: string) => {
      // Find which level this sphere is at
      const level = Object.entries(surfaceHook.spheresByColumn).find(([_, spheres]) =>
        spheres.some((s) => s.id === sphereId)
      );

      if (level) {
        //console.log('🔄 [TreeMap] Refreshing level for sphere:', sphereId, 'level:', level[0]);
        await surfaceHook.refreshLevel(Number.parseInt(level[0], 10));
      } else {
        console.warn('⚠️ [TreeMap] Could not find level for sphere:', sphereId);
      }
    },
    [surfaceHook]
  );

  /**
   * Update a sphere in the local state without reloading from API
   */
  const updateSphereInState = useCallback(
    (updatedSphere: Sphere) => {
      surfaceHook.updateSphereInState(updatedSphere);
    },
    [surfaceHook]
  );

  /**
   * Add a sphere to the local state without reloading from API
   */
  const addSphereToState = useCallback(
    (newSphere: Sphere, parentId: string) => {
      surfaceHook.addSphereToState(newSphere, parentId);
    },
    [surfaceHook]
  );

  // ============================================================================
  // Return Hook Interface
  // ============================================================================

  return {
    // Data
    context,
    spheresByColumn: surfaceHook.spheresByColumn,
    paginationState: surfaceHook.paginationState,
    sphereLookup: surfaceHook.sphereLookup,
    navigation,

    // Navigation
    drillDown,
    focusOnSphere,
    drillDownColumn,
    navigateBack,
    navigateToBreadcrumb,

    // CRUD
    createSphere,
    updateSphere,
    deleteSphere,
    moveSphere,
    convertSphere,

    // Type Conversion
    canConvert,
    getConversionRules,

    // Pagination
    loadMoreChildren,
    loadAllChildren,

    // Refresh
    refreshLevel,
    updateSphereInState,
    addSphereToState,

    // Polling (for catching status cascades, flow updates after mutations)
    pollForUpdates: surfaceHook.pollForUpdates,
    refreshSpheres: surfaceHook.refreshSpheres,

    // AI
    getSuggestions,
    acceptSuggestion,

    // State - use surface hook loading/error (context loading handled by TreeMapOrganism)
    isLoading: surfaceHook.loading,
    hasLoaded: surfaceHook.hasLoaded,
    error: error || surfaceHook.error,
  };
}

export default useTreeMap;
